import { PagenatedResponse } from '@models/common';
import { ProductInventoryClosingLog, ProductModel, ProductOption, ProductSearchQuery } from '@models/product';
import { NaverCommerceProduct } from '@models/sales-channels';
import { apiClient } from '.';

export const searchProducts = async (query?: ProductSearchQuery) => {
  return apiClient.get<PagenatedResponse<ProductModel>>(`/admin/v1/products`, {
    params: query,
  });
};

export const getProduct = async (productId: string) => {
  return apiClient.get<ProductModel>(`/admin/v1/products/${productId}`);
};

export const getDraftProduct = async (productId: string) => {
  return apiClient.get<ProductModel & { originData?: Partial<ProductModel>; productChanges?: Partial<ProductModel> }>(
    `/admin/v1/products/${productId}/draft`,
  );
};

export const createProduct = async (body: Partial<ProductModel>) => {
  return apiClient.post(`/admin/v1/products`, body);
};

export const deleteProduct = async (productId: string) => {
  return apiClient.delete(`/admin/v1/products/${productId}`);
};

export const deleteDraftProduct = async (productId: string) => {
  return apiClient.delete(`/admin/v1/products/${productId}/draft`);
};

export const getRecommendProducts = async (productId: string) => {
  return apiClient.get<ProductModel[]>(`/app/v1/products/${productId}/recommends`);
};

export const createMyFavorites = async (data: { productId: string; saleType: string }) => {
  const { productId, saleType } = data;
  return apiClient.post(`/app/v1/products/${productId}/favorite`, { saleType });
};

export const deleteMyFavorites = async (productId: string) => {
  return apiClient.delete(`/app/v1/products/${productId}/favorite`);
};

export const getProductsExcelFile = async (query?: ProductSearchQuery) => {
  return apiClient.get(`/admin/v1/products/export`, {
    params: query,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const updateProduct = async (formData: FormData) => {
  const productId = formData.get('productId');
  return apiClient.put<ProductModel>(`/admin/v1/products/${productId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getProductOptions = async (productId: string) => {
  return apiClient.get<ProductOption[]>(`/admin/v1/products/${productId}/options`);
};

export const getProductOptionsExcelFile = async (productId: string) => {
  return apiClient.get(`/admin/v1/products/${productId}/options/export`, {
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getTempProductOptions = async (productId: string) => {
  return apiClient.get<{ productOptions: ProductOption; optionLabels: string[] }>(
    `/admin/v1/products/${productId}/options-temp`,
  );
};

export const getCloseCandidateProductAndOptions = async () => {
  return apiClient.get<
    {
      _id: string;
      count: number;
      options: ProductOption[];
      remainOptions?: ProductOption[];
      product: ProductModel;
      naverProduct?: NaverCommerceProduct;
    }[]
  >(`/admin/v1/products/inventory-closures/candidates`);
};

export const closeProductOptions = async () => {
  return apiClient.post(`/admin/v1/products/inventory-closures`);
};

export const searchProductInventoryClosingHistory = async (query?: ProductSearchQuery) => {
  return apiClient.get<PagenatedResponse<ProductInventoryClosingLog>>(`/admin/v1/products/inventory-closures`, {
    params: query,
  });
};

export const batchUpdateLabels = async (data: {
  productIds: string[];
  action: 'add' | 'remove';
  labelName: string;
}) => {
  return apiClient.post(`/admin/v1/products/batch-update-labels`, data);
};

export const batchUpdateTags = async (data: { productIds: string[]; action: 'add' | 'remove'; tagName: string }) => {
  return apiClient.post(`/admin/v1/products/batch-update-tags`, data);
};

export const updateProductInventoryClosingHistoryToOrigin = async (params: {
  inventoryClosingLogId: string;
  action: 'apply' | 'restore';
}) => {
  return apiClient.put(`/admin/v1/products/inventory-closures/${params.inventoryClosingLogId}/${params.action}`);
};

export const createNaverProduct = async (formData: FormData) => {
  const productId = formData.get('productId');
  return apiClient.post(`/admin/v1/products/${productId}/naver-store`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const refreshNaverProduct = async (productId: string) => {
  return apiClient.post(`/admin/v1/products/${productId}/naver-store/refresh`);
};

export const createEzwelProduct = async (formData: FormData) => {
  const productId = formData.get('productId');
  return apiClient.post(`/admin/v1/products/${productId}/ezwel`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const requestEzwelProductApproval = async (productId: string) => {
  return apiClient.post(`/admin/v1/products/${productId}/ezwel/approval`);
};

export const syncEzwelProduct = async (formData: FormData) => {
  const productId = formData.get('productId');
  return apiClient.post(`/admin/v1/products/${productId}/ezwel/sync`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const updateEzwelProductStatus = async (productId: string, status: 'open' | 'close') => {
  return apiClient.post(`/admin/v1/products/${productId}/ezwel/status`, { status });
};

export const unlinkEzwelProduct = async (productId: string) => {
  return apiClient.post(`/admin/v1/products/${productId}/ezwel/unlink`);
};

export const rotateEzwelCspGdsCd = async (productId: string) => {
  return apiClient.post(`/admin/v1/products/${productId}/ezwel/csp-gds-cd`);
};

export const getEzwelCategories = async (params?: { chCd?: string; ctgrCd?: string; highCtgrCd?: string }) => {
  return apiClient.get(`/admin/v1/ezwel/categories`, {
    params: { chCd: params?.chCd || '102', ...params },
  });
};

export const getEzwelAnnouncements = async (gdsAnmtClsCd: string) => {
  return apiClient.get(`/admin/v1/ezwel/announcements`, { params: { gdsAnmtClsCd } });
};

// 상품 클러스터링 관련 API
export const performProductClustering = async (forceRecluster?: boolean) => {
  return apiClient.post(`/admin/v1/products/clustering/perform`, null, {
    params: { force_recluster: forceRecluster },
  });
};

export const getClusteringResults = async () => {
  return apiClient.get(`/admin/v1/products/clustering/results`);
};

export const getClusteringStatistics = async () => {
  return apiClient.get(`/admin/v1/products/clustering/statistics`);
};

export const getProductsByCategory = async (category: string) => {
  return apiClient.get(`/admin/v1/products/clustering/category/${encodeURIComponent(category)}`);
};

export const getProductsByLocation = async (location: string) => {
  return apiClient.get(`/admin/v1/products/clustering/location/${encodeURIComponent(location)}`);
};

export const getProductsByAgeGroup = async (ageGroup: string) => {
  return apiClient.get(`/admin/v1/products/clustering/age-group/${encodeURIComponent(ageGroup)}`);
};

export const getAvailableCategories = async () => {
  return apiClient.get(`/admin/v1/products/clustering/categories`);
};

export const getAvailableLocations = async () => {
  return apiClient.get(`/admin/v1/products/clustering/locations`);
};

export const getAvailableAgeGroups = async () => {
  return apiClient.get(`/admin/v1/products/clustering/age-groups`);
};

export const getProductsByClusterId = async (clusterId: number) => {
  return apiClient.get(`/admin/v1/products/clustering/cluster/${clusterId}`);
};
